import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI, usersAPI, productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineDownload,
  HiOutlineDocumentReport,
  HiOutlineChevronRight,
  HiOutlineCheck,
  HiOutlineSearch,
  HiOutlineTable,
  HiOutlinePhone,
  HiOutlineUserGroup,
  HiOutlineClipboardList,
  HiOutlineCalendar,
  HiOutlineLocationMarker,
} from 'react-icons/hi';

const DATA_SOURCES = [
  { key: 'leads', label: 'Leads', icon: HiOutlineDocumentReport, description: 'Lead details with custom fields' },
  { key: 'calls', label: 'Call Logs', icon: HiOutlinePhone, description: 'Call history with caller and lead info' },
  { key: 'followups', label: 'Follow-ups', icon: HiOutlineClipboardList, description: 'Follow-up schedule and completion data' },
  { key: 'team', label: 'Team Performance', icon: HiOutlineUserGroup, description: 'Telecaller stats and conversion rates' },
  { key: 'visits', label: 'Visits & Meetings', icon: HiOutlineLocationMarker, description: 'Site visits / meetings with time spent and outcome' },
];

const FIELD_OPTIONS = {
  leads: [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'company', label: 'Company' },
    { key: 'source', label: 'Source' },
    { key: 'stage', label: 'Stage' },
    { key: 'status', label: 'Status' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'product', label: 'Product' },
    { key: 'notes', label: 'Notes' },
    { key: 'createdAt', label: 'Created Date' },
    { key: 'updatedAt', label: 'Updated Date' },
  ],
  calls: [
    { key: 'callerName', label: 'Caller' },
    { key: 'leadName', label: 'Lead' },
    { key: 'leadPhone', label: 'Lead Phone' },
    { key: 'callType', label: 'Call Type' },
    { key: 'status', label: 'Status' },
    { key: 'duration', label: 'Duration (s)' },
    { key: 'notes', label: 'Notes' },
    { key: 'createdAt', label: 'Date' },
  ],
  followups: [
    { key: 'leadName', label: 'Lead' },
    { key: 'leadPhone', label: 'Lead Phone' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'status', label: 'Status' },
    { key: 'type', label: 'Type' },
    { key: 'scheduledDate', label: 'Scheduled Date' },
    { key: 'completedAt', label: 'Completed Date' },
    { key: 'notes', label: 'Notes' },
  ],
  team: [
    { key: 'name', label: 'Name' },
    { key: 'role', label: 'Role' },
    { key: 'totalCalls', label: 'Total Calls' },
    { key: 'connectedCalls', label: 'Connected' },
    { key: 'notConnected', label: 'Not Connected' },
    { key: 'avgDuration', label: 'Avg Duration (s)' },
    { key: 'totalLeads', label: 'Total Leads' },
    { key: 'convertedLeads', label: 'Converted' },
    { key: 'followUpsCompleted', label: 'Follow-ups Done' },
    { key: 'demoScheduled', label: 'Demos Scheduled' },
    { key: 'conversionRate', label: 'Conversion Rate' },
    { key: 'visitsScheduled', label: 'Visits Scheduled' },
    { key: 'visitsCompleted', label: 'Visits Done' },
    { key: 'visitsMissed', label: 'Visits Missed' },
    { key: 'visitMinutes', label: 'Visit Time (min)' },
    { key: 'productiveMinutes', label: 'Productive Time (min)' },
    { key: 'utilisation', label: 'Utilisation' },
  ],
  visits: [
    { key: 'scheduledStart', label: 'Scheduled' },
    { key: 'startedAt', label: 'Started' },
    { key: 'endedAt', label: 'Ended' },
    { key: 'employee', label: 'Employee' },
    { key: 'leadName', label: 'Customer' },
    { key: 'leadPhone', label: 'Customer Phone' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'plannedMinutes', label: 'Planned (min)' },
    { key: 'actualMinutes', label: 'Actual (min)' },
    { key: 'lateMinutes', label: 'Late (min)' },
    { key: 'outcome', label: 'Outcome' },
    { key: 'outcomeNotes', label: 'Visit Notes' },
    { key: 'nextFollowUpAt', label: 'Next Follow-up' },
    { key: 'repeatVisit', label: 'Repeat Visit' },
    { key: 'address', label: 'Address' },
    { key: 'scheduledBy', label: 'Scheduled By' },
  ],
};

const DATE_RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'custom', label: 'Custom Range' },
];

// Day-wise summary mode: one row per day in the selected date range with
// aggregated stats. The Date column is always included.
const DAYWISE_FIELD_OPTIONS = {
  leads: [
    { key: 'date', label: 'Date', fixed: true },
    { key: 'newLeads', label: 'New Leads' },
    { key: 'converted', label: 'Converted' },
    { key: 'lost', label: 'Lost' },
    { key: 'demoScheduled', label: 'Demos Scheduled' },
  ],
  calls: [
    { key: 'date', label: 'Date', fixed: true },
    { key: 'totalCalls', label: 'Total Calls' },
    { key: 'connected', label: 'Connected' },
    { key: 'notConnected', label: 'Not Connected' },
    { key: 'totalDuration', label: 'Total Duration (s)' },
    { key: 'avgDuration', label: 'Avg Duration (s)' },
  ],
  followups: [
    { key: 'date', label: 'Date', fixed: true },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'completed', label: 'Completed' },
    { key: 'pending', label: 'Pending' },
    { key: 'cancelled', label: 'Cancelled' },
  ],
  team: [
    { key: 'date', label: 'Date', fixed: true },
    { key: 'totalCalls', label: 'Total Calls' },
    { key: 'connected', label: 'Connected' },
    { key: 'notConnected', label: 'Not Connected' },
    { key: 'newLeads', label: 'New Leads' },
    { key: 'convertedLeads', label: 'Converted' },
    { key: 'followUpsCompleted', label: 'Follow-ups Done' },
    { key: 'visitsCompleted', label: 'Visits Done' },
    { key: 'visitMinutes', label: 'Visit Time (min)' },
  ],
  visits: [
    { key: 'date', label: 'Date', fixed: true },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'completed', label: 'Completed' },
    { key: 'noShow', label: 'No Show' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'visitMinutes', label: 'Visit Time (min)' },
    { key: 'avgVisitMinutes', label: 'Avg Visit (min)' },
  ],
};

const REPORT_TYPES = [
  { key: 'records', label: 'Detailed Records', icon: HiOutlineTable, description: 'One row per record with the fields you select' },
  { key: 'day', label: 'Day-wise Summary', icon: HiOutlineCalendar, description: 'One row per day with daily totals (e.g. 1 Sept → 26 Sept)' },
];

const LEAD_STAGES = ['new', 'contacted', 'interested', 'demo_scheduled', 'negotiation', 'converted', 'closed', 'lost'];
const CALL_STATUSES = ['connected', 'not_connected', 'no_answer', 'busy', 'switched_off', 'wrong_number', 'not_reachable'];
const FOLLOWUP_STATUSES = ['pending', 'completed', 'cancelled'];
const VISIT_STATUSES = ['scheduled', 'pending_approval', 'in_progress', 'completed', 'no_show', 'cancelled', 'rejected', 'rescheduled'];

const CustomReportBuilder = () => {
  const [step, setStep] = useState(1); // 1: data source, 2: fields, 3: filters, 4: preview, 5: export
  const [dataSource, setDataSource] = useState('');
  const [reportType, setReportType] = useState('records'); // 'records' | 'day'
  const [selectedFields, setSelectedFields] = useState([]);
  const [dateRange, setDateRange] = useState({ range: 'month', startDate: '', endDate: '' });
  const [filters, setFilters] = useState({ source: 'all', product: 'all', employee: 'all', stage: 'all', status: 'all' });
  const [exporting, setExporting] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Fetch employees and products for filters.
  // NOTE: /api/users paginates server-side with a default limit of 20, so a
  // high limit is passed to list every employee of the organization.
  const { data: employeesData } = useQuery({
    queryKey: ['users-for-custom-report'],
    queryFn: () => usersAPI.getAll({ limit: 1000 }),
    select: (res) => res.data?.data || res.data || [],
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-for-custom-report'],
    queryFn: () => productsAPI.getAll(),
    select: (res) => res.data?.data || res.data?.products || res.data || [],
  });

  const employees = useMemo(() => {
    if (!employeesData) return [];
    const arr = Array.isArray(employeesData) ? employeesData : employeesData.users || [];
    // Show ALL organization users (admin, manager, telecaller, …) so the
    // Employee filter lists every employee — not just telecallers/managers.
    return arr;
  }, [employeesData]);

  const products = useMemo(() => {
    if (!productsData) return [];
    return Array.isArray(productsData) ? productsData : productsData.products || [];
  }, [productsData]);

  const fieldOptions = dataSource
    ? (reportType === 'day' ? DAYWISE_FIELD_OPTIONS[dataSource] || [] : FIELD_OPTIONS[dataSource] || [])
    : [];

  // Switching report type swaps the field list, so reset the selection to a
  // sensible default for the new mode (day-wise pre-checks every column).
  const changeReportType = (type) => {
    setReportType(type);
    if (type === 'day' && dataSource) {
      setSelectedFields((DAYWISE_FIELD_OPTIONS[dataSource] || []).map(f => f.key));
    } else {
      setSelectedFields([]);
    }
  };

  // Also pre-select the day-wise columns when the data source is picked
  // while already in day mode (and clear stale selections).
  const changeDataSource = (key) => {
    setDataSource(key);
    if (reportType === 'day') {
      setSelectedFields((DAYWISE_FIELD_OPTIONS[key] || []).map(f => f.key));
    } else {
      setSelectedFields([]);
    }
  };

  // Fetch preview data via the dedicated preview endpoint. It applies the
  // date range + filters properly for every data source (the generic list
  // APIs ignore date params and scope follow-ups to the logged-in user,
  // which left the preview empty) and supports both records and day-wise
  // grouping.
  const fetchPreviewData = useCallback(async () => {
    if (!dataSource || selectedFields.length === 0) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await reportsAPI.previewCustom({
        dataSource,
        fields: selectedFields,
        dateRange: {
          range: dateRange.range,
          startDate: dateRange.range === 'custom' && dateRange.startDate ? new Date(dateRange.startDate).toISOString() : undefined,
          endDate: dateRange.range === 'custom' && dateRange.endDate ? new Date(dateRange.endDate).toISOString() : undefined,
        },
        filters,
        groupBy: reportType,
      });

      let rows = response.data?.data || [];
      // Normalize blanks to "—" for display, but keep real numbers (day-wise
      // zero days must show 0, not a dash).
      rows = rows.map((row) => {
        const out = {};
        Object.keys(row).forEach((key) => {
          const val = row[key];
          out[key] =
            val === undefined || val === null || (typeof val === 'string' && !val.trim())
              ? '—'
              : val;
        });
        return out;
      });
      setPreviewData(rows);
    } catch (error) {
      console.error('Preview fetch error:', error);
      setPreviewError(error.response?.data?.message || error.message || 'Failed to load preview data');
      setPreviewData([]);
    } finally {
      setPreviewLoading(false);
    }
  }, [dataSource, selectedFields, filters, dateRange, reportType]);

  // Auto-fetch preview when entering step 4
  useEffect(() => {
    if (step === 4) {
      fetchPreviewData();
    }
  }, [step, fetchPreviewData]);

  // Reset preview state when selections change (user went back and changed something)
  useEffect(() => {
    if (step < 4) {
      setPreviewData([]);
      setPreviewError(null);
    }
  }, [step, dataSource, selectedFields, filters, dateRange, reportType]);

  const toggleField = (key) => {
    // In day-wise mode the Date column is the axis of the report — it can't
    // be unchecked.
    if (reportType === 'day' && fieldOptions.find(f => f.key === key)?.fixed) return;
    setSelectedFields(prev =>
      prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
    );
  };

  const selectAllFields = () => setSelectedFields(fieldOptions.map(f => f.key));
  const deselectAllFields = () => setSelectedFields([]);

  const handleExport = async () => {
    if (!dataSource) { toast.error('Select a data source'); return; }
    if (selectedFields.length === 0) { toast.error('Select at least one field'); return; }

    setExporting(true);
    try {
      const config = {
        dataSource,
        fields: selectedFields,
        dateRange: {
          range: dateRange.range,
          startDate: dateRange.range === 'custom' && dateRange.startDate ? new Date(dateRange.startDate).toISOString() : undefined,
          endDate: dateRange.range === 'custom' && dateRange.endDate ? new Date(dateRange.endDate).toISOString() : undefined,
        },
        filters,
        groupBy: reportType === 'day' ? 'day' : undefined,
      };

      const response = await reportsAPI.exportCustom(config);

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${reportType === 'day' ? 'daywise-report' : 'custom-report'}-${dataSource}-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Custom report downloaded successfully!');
    } catch (error) {
      console.error('Custom report export error:', error);
      toast.error(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setExporting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return !!dataSource;
    if (step === 2) return selectedFields.length > 0;
    if (step === 3) return true;
    if (step === 4) return true;
    return true;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <HiOutlineTable className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Custom Report Builder</h2>
          <p className="text-sm text-gray-500">Build custom reports by selecting data source, fields, and filters</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 py-2">
        {[
          { n: 1, label: 'Data Source' },
          { n: 2, label: 'Fields' },
          { n: 3, label: 'Filters' },
          { n: 4, label: 'Preview' },
          { n: 5, label: 'Export' },
        ].map((s, idx) => (
          <React.Fragment key={s.n}>
            <button
              onClick={() => { if (s.n < step || (s.n === step + 1 && canProceed())) setStep(s.n); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                step === s.n ? 'bg-primary-600 text-white shadow-sm'
                : step > s.n ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {step > s.n ? <HiOutlineCheck className="w-4 h-4" /> : <span className="w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs">{s.n}</span>}
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {idx < 4 && <HiOutlineChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Data Source */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {DATA_SOURCES.map(ds => {
              const Icon = ds.icon;
              const selected = dataSource === ds.key;
              return (
                <button
                  key={ds.key}
                  onClick={() => changeDataSource(ds.key)}
                  className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                    selected ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selected ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{ds.label}</h3>
                      <p className="text-xs text-gray-500">{ds.description}</p>
                    </div>
                  </div>
                  {selected && <div className="mt-2 text-xs text-primary-600 font-medium">✓ Selected</div>}
                </button>
              );
            })}
          </div>

          {/* Report Type — detailed rows or a day-wise summary */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Report Type</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {REPORT_TYPES.map(rt => {
                const Icon = rt.icon;
                const selected = reportType === rt.key;
                return (
                  <button
                    key={rt.key}
                    onClick={() => changeReportType(rt.key)}
                    className={`p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                      selected ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selected ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{rt.label}</h3>
                        <p className="text-xs text-gray-500">{rt.description}</p>
                      </div>
                    </div>
                    {selected && <div className="mt-2 text-xs text-primary-600 font-medium">✓ Selected</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Fields */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Select Fields to Include</h3>
            <div className="flex gap-2">
              <button onClick={selectAllFields} className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors">Select All</button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button onClick={deselectAllFields} className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors">Deselect All</button>
            </div>
          </div>
          <p className="text-sm text-gray-500">Choose which columns appear in your exported report. {selectedFields.length} field(s) selected.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {fieldOptions.map(field => {
              const checked = selectedFields.includes(field.key);
              return (
                <label
                  key={field.key}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                    checked ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleField(field.key)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className={`text-sm font-medium ${checked ? 'text-primary-700' : 'text-gray-700'}`}>{field.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Filters */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Filter Your Data</h3>
          <p className="text-sm text-gray-500">Apply filters to narrow down the data in your report. All filters are optional.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Date Range</label>
              <select
                value={dateRange.range}
                onChange={(e) => setDateRange(prev => ({ ...prev, range: e.target.value }))}
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              >
                {DATE_RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>

            {dateRange.range === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">End Date</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
                  />
                </div>
              </>
            )}

            {/* Employee Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Employee</label>
              <select
                value={filters.employee}
                onChange={(e) => setFilters(prev => ({ ...prev, employee: e.target.value }))}
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              >
                <option value="all">All Employees</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {`${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email}
                    {emp.role ? ` (${emp.role})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Filter (leads only) */}
            {dataSource === 'leads' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Source</label>
                <select
                  value={filters.source}
                  onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
                >
                  <option value="all">All Sources</option>
                  <option value="website">Website</option>
                  <option value="referral">Referral</option>
                  <option value="social_media">Social Media</option>
                  <option value="cold_call">Cold Call</option>
                  <option value="advertisement">Advertisement</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {/* Stage Filter (leads only) */}
            {dataSource === 'leads' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Stage</label>
                <select
                  value={filters.stage}
                  onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
                >
                  <option value="all">All Stages</option>
                  {LEAD_STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                </select>
              </div>
            )}

            {/* Status Filter (calls & follow-ups) */}
            {(dataSource === 'calls' || dataSource === 'followups' || dataSource === 'visits') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
                >
                  <option value="all">All Statuses</option>
                  {(dataSource === 'calls' ? CALL_STATUSES : dataSource === 'visits' ? VISIT_STATUSES : FOLLOWUP_STATUSES).map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Product Filter (not used for visits) */}
            {dataSource !== 'visits' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Product</label>
              <select
                value={filters.product}
                onChange={(e) => setFilters(prev => ({ ...prev, product: e.target.value }))}
                className="w-full px-3.5 py-2.5 rounded-xl border-2 border-gray-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
              >
                <option value="all">All Products</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>{p.name}</option>
                ))}
              </select>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Step 4: Preview */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Data Preview</h3>
              <p className="text-sm text-gray-500">
                {previewLoading
                  ? 'Loading data...'
                  : previewData.length > 0
                    ? reportType === 'day'
                      ? `One row per day — showing ${previewData.length} day(s)`
                      : `Showing ${previewData.length} record(s)`
                    : 'Preview of your report data'}
              </p>
            </div>
            <button
              onClick={fetchPreviewData}
              disabled={previewLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className={`w-4 h-4 ${previewLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              Refresh
            </button>
          </div>

          {/* Loading state */}
          {previewLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="animate-spin h-8 w-8 text-primary-500 mb-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm text-gray-500">Loading preview data...</p>
            </div>
          )}

          {/* Error state */}
          {previewError && !previewLoading && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-800">Failed to load preview</p>
                  <p className="text-xs text-yellow-700 mt-1">{previewError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!previewLoading && !previewError && previewData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <HiOutlineTable className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500">No data found matching your filters.</p>
              <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or date range.</p>
            </div>
          )}

          {/* Desktop Table */}
          {!previewLoading && previewData.length > 0 && (
            <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    {selectedFields.map(key => {
                      const field = FIELD_OPTIONS[dataSource]?.find(f => f.key === key);
                      return (
                        <th key={key} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          {field?.label || key}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {previewData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-400">{idx + 1}</td>
                      {selectedFields.map(key => (
                        <td key={key} className="px-4 py-3 text-sm text-gray-700 max-w-[200px] truncate" title={row[key]}>
                          {row[key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile Cards */}
          {!previewLoading && previewData.length > 0 && (
            <div className="md:hidden divide-y divide-gray-100">
              {previewData.map((row, idx) => (
                <div key={idx} className="p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-gray-400">#{idx + 1}</p>
                  {selectedFields.map(key => {
                    const field = FIELD_OPTIONS[dataSource]?.find(f => f.key === key);
                    return (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-gray-500">{field?.label || key}</span>
                        <span className="text-gray-900 font-medium text-right max-w-[60%] truncate" title={row[key]}>{row[key]}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Footer note */}
          {!previewLoading && previewData.length > 0 && reportType !== 'day' && (
            <p className="text-xs text-gray-400 text-center">
              Showing first 50 records. The full export will include all matching records.
            </p>
          )}
          {!previewLoading && previewData.length > 0 && reportType === 'day' && (
            <p className="text-xs text-gray-400 text-center">
              Every day in the selected range is listed — days with no activity show 0.
            </p>
          )}
        </div>
      )}

      {/* Step 5: Export */}
      {step === 5 && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Report Summary</h3>
            <p className="text-sm text-gray-500">Review your report configuration before exporting.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Data Source</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{DATA_SOURCES.find(d => d.key === dataSource)?.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{reportType === 'day' ? 'Day-wise summary (one row per day)' : 'Detailed records'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date Range</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{DATE_RANGES.find(r => r.key === dateRange.range)?.label}{dateRange.range === 'custom' && dateRange.startDate ? ` (${dateRange.startDate} → ${dateRange.endDate})` : ''}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fields ({selectedFields.length})</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {selectedFields.map(key => {
                    const field = fieldOptions.find(f => f.key === key);
                    return field ? (
                      <span key={key} className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">{field.label}</span>
                    ) : null;
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filters</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {filters.employee !== 'all' && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">Employee: {employees.find(e => e._id === filters.employee)?.firstName || 'Selected'}</span>}
                  {filters.source !== 'all' && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-100 text-green-700 text-xs font-medium">Source: {filters.source}</span>}
                  {filters.stage !== 'all' && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-yellow-100 text-yellow-700 text-xs font-medium">Stage: {filters.stage}</span>}
                  {filters.status !== 'all' && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 text-xs font-medium">Status: {filters.status}</span>}
                  {filters.product !== 'all' && <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 text-xs font-medium">Product: {products.find(p => p._id === filters.product)?.name || 'Selected'}</span>}
                  {filters.employee === 'all' && filters.source === 'all' && filters.stage === 'all' && filters.status === 'all' && filters.product === 'all' && (
                    <span className="text-xs text-gray-400">No filters applied — all data included</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Export Format */}
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-6 text-center">
            <HiOutlineDownload className="w-10 h-10 text-primary-500 mx-auto mb-3" />
            <h4 className="text-lg font-bold text-gray-900 mb-1">Ready to Export</h4>
            <p className="text-sm text-gray-500 mb-4">Your custom report will be generated as an Excel (.xlsx) file.</p>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold text-sm hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  Generating Report...
                </>
              ) : (
                <>
                  <HiOutlineDownload className="w-4 h-4" />
                  Download Excel Report
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        {step > 1 ? (
          <button
            onClick={() => setStep(s => s - 1)}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            ← Back
          </button>
        ) : <div />}
        {step < 5 && (
          <button
            onClick={() => setStep(s => s + 1)}
            disabled={!canProceed()}
            className="px-5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomReportBuilder;